import { Link, useLocation } from 'react-router-dom';

function Logo() {
  return (
    <div className="flex items-center space-x-2.5">
      <div className="relative w-10 h-12 flex items-center justify-center">
        {/* Magazine shape */}
        <div className="absolute inset-0 bg-white rounded-sm shadow-md overflow-hidden">
          {/* Gradient wave */}
          <svg viewBox="0 0 40 48" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="footerLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6B4EFF" />
                <stop offset="50%" stopColor="#9B6DFF" />
                <stop offset="100%" stopColor="#FF4D7D" />
              </linearGradient>
            </defs>
            {/* Wave pattern */}
            <path
              d="M0 32 Q10 28, 20 32 T40 32 L40 48 L0 48 Z"
              fill="url(#footerLogoGradient)"
            />
            <path
              d="M0 36 Q10 32, 20 36 T40 36 L40 48 L0 48 Z"
              fill="url(#footerLogoGradient)"
              opacity="0.6"
            />
          </svg>
          {/* Magazine lines */}
          <div className="absolute top-2 left-2 right-2 space-y-1">
            <div className="h-1 bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] rounded-full w-4/5"></div>
            <div className="h-0.5 bg-gray-300 rounded-full w-full"></div>
            <div className="h-0.5 bg-gray-300 rounded-full w-3/4"></div>
            <div className="h-0.5 bg-gray-200 rounded-full w-5/6"></div>
          </div>
        </div>
        {/* Spine effect */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#6B4EFF] to-[#FF4D7D] rounded-l-sm"></div>
      </div>
      <span className="font-semibold text-lg text-gray-900">Harmony Horizon</span>
    </div>
  );
}

export default function Footer() {
  const location = useLocation();
  const isAdvertisersPage = location.pathname === '/advertisers';

  // Removed TypeScript type annotation for compatibility with plain JavaScript
  const scrollToSection = (id) => {
    if (typeof document === 'undefined') {
      // Guard for environments without a DOM (e.g., SSR)
      return;
    }

    const element = document.getElementById(id);
    if (element) {
      try {
        element.scrollIntoView({ behavior: 'smooth' });
      } catch (error) {
        // Fallback for browsers that might not support the options object
        element.scrollIntoView();
        console.error('Smooth scrolling failed:', error);
      }
    } else {
      console.warn(`Element with id "${id}" not found.`);
    }
  };

  return (
    <footer className="bg-[#FAFAFA] text-gray-900 py-12 border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <Link to="/" className="flex items-center mb-4 cursor-pointer">
              <Logo />
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed">
              Sponsor-funded monthly magazines for senior living communities. No facility budget required.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-gray-900">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              {!isAdvertisersPage ? (
                <>
                  <li>
                    <button
                      onClick={() => scrollToSection('why-partner')}
                      className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                    >
                      Why partner with us
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollToSection('how-its-funded')}
                      className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                    >
                      How it&apos;s funded
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollToSection('request-sample')}
                      className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                    >
                      Request Sample
                    </button>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <button
                      onClick={() => scrollToSection('how-it-works')}
                      className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                    >
                      How it works
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => scrollToSection('advertiser-form')}
                      className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                    >
                      Get Started
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-gray-900">For Partners</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to="/"
                  className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                >
                  Facilities
                </Link>
              </li>
              <li>
                <Link
                  to="/advertisers"
                  className="text-gray-600 hover:text-[#6B4EFF] transition-colors cursor-pointer"
                >
                  Advertisers
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-4 text-gray-900">Contact</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>
                <a
                  href="mailto:Social@MedHive.media"
                  className="hover:text-[#6B4EFF] transition-colors"
                >
                  Social@MedHive.media
                </a>
              </li>
              <li>
                <a href="tel:+17207124544" className="hover:text-[#6B4EFF] transition-colors">
                  +1 720 712 4544
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
          <p>© 2025 Harmony Horizon. All rights reserved.</p>
          <div className="flex items-center space-x-4 mt-4 md:mt-0">
            <a href="#" className="hover:text-[#6B4EFF] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-[#6B4EFF] transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
